import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createDatabase, clearDatabase, LancarPOSDatabase } from '@lancar-pos/db-schema';
import { migrateFromZustand } from './migrations/migrateFromZustand';

interface DatabaseContextType {
  db: LancarPOSDatabase | null;
  isLoading: boolean;
  error: Error | null;
  clearAllData: () => Promise<void>;
}

const DatabaseContext = createContext<DatabaseContextType>({
  db: null,
  isLoading: true,
  error: null,
  clearAllData: async () => {},
});

export function DatabaseProvider({ children }: { children: ReactNode }) {
  const [db, setDb] = useState<LancarPOSDatabase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const clearAllData = async () => {
    if (!db) return;
    await clearDatabase(db);
  };

  useEffect(() => {
    let mounted = true;

    async function initDatabase() {
      try {
        setIsLoading(true);

        const env = import.meta.env.VITE_ENV || '';
        const dbName = env ? `${env}_lancarpos` : 'dev_lancarpos';
        const database = await createDatabase(dbName);

        if (!mounted) return;

        await migrateFromZustand(database);

        setDb(database);
        setError(null);
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize database'));
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    initDatabase();

    return () => {
      mounted = false;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Menginisialisasi database...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Gagal Memuat Database</h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Muat Ulang
          </button>
        </div>
      </div>
    );
  }

  return (
    <DatabaseContext.Provider value={{ db, isLoading, error, clearAllData }}>
      {children}
    </DatabaseContext.Provider>
  );
}

export function useDatabase(): LancarPOSDatabase {
  const { db, isLoading } = useContext(DatabaseContext);

  if (isLoading) {
    throw new Error('Database is still loading');
  }

  if (!db) {
    throw new Error('Database not initialized');
  }

  return db;
}

export function useDatabaseStatus() {
  const { isLoading, error } = useContext(DatabaseContext);
  return { isLoading, error };
}
